import { gql } from "@medbillai/generated-graphql-types";

import { CURRENT_BENEFITS_SUMMARY_SCHEMA_VERSION } from "@medbillai/schemas";

export const getIssueQueryVariables = (issueId: string, userId: string) => {
  return {
    id: issueId,
    benefitsSummarySchemaVersion: CURRENT_BENEFITS_SUMMARY_SCHEMA_VERSION,
    userId,
  };
};

export const getIssueQuery = gql(/* GraphQL */ `
  query GetIssue($id: ID!, $benefitsSummarySchemaVersion: Int!, $userId: ID!) {
    issue__Issue(filter: {
      id: { eq: $id },
      owner: { id: { eq: $userId } }
    }) {
      __typename
      id
      title
      openedAt
      closedAt
      caseType
      benefitsQuestionType
      billDisputeType
      additionalDetails
      benefitsInclude
      benefitsPrice
      isEscalated
      completedPostEscalationActions
      hasStaffParticipant
      benefitsCoverage
      userVisibleEvents(order: { sentAt: { dir: ASC } }) {
        # __typename seems to be needed for the polymorphic query here.
        __typename
        id
        eventType
        participant
        sentAt
        userVisible
        sentByProfile {
          name
        }
        eventFeedbacks (filter:  {owner: {id: {eq: $userId}}}) {
          id
          rating
        }

        ... on issue__DocumentUploadedEvent_Type {
          uploading @client
          uploadFailed @client
          document {
            id
            name
            dataType
            createdAt
            latestIngestion {
              endedAt
              resources(filter: { isLatest: { eq: true } }, first: 1) {
                __typename
                id
                ... on fhirext__InvoiceVersion_Type {
                  invoiceTitle: title
                }
                ... on fhirext__ExplanationOfBenefitVersion_Type {
                  eobTitle: title
                }
              }
            }
            files {
              id
              sizeBytes
              contentType
            }
          }
        }
        ... on issue__ChatEvent_Type {
          chatBody
        }
        ... on issue__HipaaReleaseRequestedEvent_Type {
          hipaaRelease {
            id
            name
            dateOfBirth
            createdAt
            grantedAt
          }
        }
        ... on issue__BillDisputeEvent_Type {
          eobId
          healthRecordKind
          healthRecordKey
          eob {
            id
            resourceId
            provider {
              display
            }
            facility {
              display
            }
            serviceDate
            decision {
              text
              coding {
                code
              }
            }
            findings(
              filter: {
                and: [
                  {
                    explanation: { exists: true }
                    lastReviewStatus: { eq: ACCEPTED }
                    userDismissed: { eq: false }
                  }
                ]
              }
            ) {
              id
              type
            }
          }
        }
        ... on issue__TemplateReleaseRequestedEvent_Type {
          release {
            id
            createdAt
            grantedAt
          }
          template {
            id
            templateType
            displayName
            description
          }
        }
        ... on issue__ClosedEvent_Type {
          userClosureReason
          feedback {
            id
            rating
          }
        }
        ... on issue__PaymentAgreementRequestedEvent_Type {
          userAccepted
        }
        ... on issue__ClosurePaymentRequestedEvent_Type {
          dollarAmountSaved
          totalBillDollarAmount
          successFee
          chargeDelayDays
          sentAt
          chargeDate
        }
        ... on issue__BenefitsSummaryGeneratedEvent_Type {
          id
          sentAt
          plan {
            id
            planLabel
            link {
              id
              endpoint {
                id
                slug
              }
            }
            benefitsSummaryResults(
              filter: { schemaVersion: { eq: $benefitsSummarySchemaVersion } }
            ) {
              id
              summary
            }
          }
        }
        ... on issue__PlanSelectionEvent_Type {
          id
          selectedPlan {
            id
          }
          options {
            id
            planLabel
          }
        }
        ... on issue__UserSelectionRequestedEvent_Type {
          id
          stringMessage: message
          stringOptions: options
        }
        ... on issue__UserMultiSelectionRequestedEvent_Type {
          id
          multiMessage: message
          multiOptions: options
        }
        ... on issue__ConnectInsuranceRequestedEvent_Type {
          id
          sentAt
          insuranceLinkMessage
        }
        ... on issue__ConnectAccountRequestedEvent_Type {
          id
          sentAt
          connectAccountMessage: chatBody
          endpointType
          buttonText
          showSkipButton
        }
        ... on issue__BillPayRequestedEvent_Type {
          # Aliased to disambiguate from BillPaymentRequestedEvent.acceptedAt;
          # gel-graphql collapses same-named fields across inline fragments to
          # a single EdgeQL projection, returning null for the wrong subtype.
          billPayAcceptedAt: acceptedAt
          declinedAt
          availableMinimumDiscountOptions
        }
        ... on issue__BillPaySuccessEvent_Type {
          negotiatedBillAmount
          originalBillAmount
          successFeeAmount
          successFeePercent
          totalCharged
        }
        ... on issue__BillPaymentRequestedEvent_Type {
          billPaymentAcceptedAt: acceptedAt
          billPaid
        }
        ... on issue__BillPaymentSuccessEvent_Type {
          stripePaymentIntentId
          billPaymentRequest {
            id
          }
        }
        ... on issue__DocumentAnalysisEvent_Type {
          status
          errorMessage
          errorIsRetriable
        }
        ... on issue__HAAssignedEvent_Type {
          assigneeProfile {
            name
          }
          assigneeAverageCaseRating
        }
        ... on issue__PostEscalationActionsEvent_Type {
          actions(order: { isRequired: { dir: DESC } }) {
            __typename
            isRequired
            ... on issue__UploadBillPostEscalationAction_Type {
              uploadBillLinkedEvent: linkedEvent {
                document {
                  id
                  name
                  files {
                    id
                    contentType
                  }
                }
              }
            }
            ... on issue__UploadEobPostEscalationAction_Type {
              uploadEobLinkedEvent: linkedEvent {
                document {
                  id
                  name
                  files {
                    id
                    contentType
                  }
                }
              }
            }
            ... on issue__UploadMemberIdPostEscalationAction_Type {
              uploadMemberIdLinkedEvent: linkedEvent {
                document {
                  id
                  name
                  files {
                    id
                    contentType
                  }
                }
              }
            }
            ... on issue__SignTemplatePostEscalationAction_Type {
              signLinkedEvent: linkedEvent {
                id
                release {
                  id
                }
                template {
                  id
                  templateType
                  displayName
                }
              }
            }
            ... on issue__PaymentAgreementPostEscalationAction_Type {
              payLinkedEvent: linkedEvent {
                id
                userAccepted
              }
            }
            ... on issue__BillPayPostEscalationAction_Type {
              billPayLinkedEvent: linkedEvent {
                id
                acceptedAt
                declinedAt
                availableMinimumDiscountOptions
              }
            }
            ... on issue__ConnectInsurancePostEscalationAction_Type {
              insLinkedEvent: linkedEvent {
                id
                endpointType
              }
            }
            ... on issue__ConnectEhrPostEscalationAction_Type {
              ehrLinkedEvent: linkedEvent {
                id
                endpointType
              }
            }
          }
        }
      }
      lastEvent {
        __typename
        id
        sentAt
        eventType
        participant
        ... on issue__ChatEvent_Type {
          chatBody
        }
        ... on issue__TemplateReleaseRequestedEvent_Type {
          template {
            description
          }
        }
        ... on issue__BillPaymentRequestedEvent_Type {
          acceptedAt
          billPaid
        }
        ... on issue__ClosurePaymentRequestedEvent_Type {
          dollarAmountSaved
          totalBillDollarAmount
          successFee
          chargeDelayDays
          sentAt
        }
        ... on issue__DocumentAnalysisEvent_Type {
          status
        }
      }
      lastNonUserEvent {
        __typename
        id
        participant
      }
      firstUnseenEvent {
        __typename
        id
        sentAt
        eventType
        participant
        ... on issue__ChatEvent_Type {
          chatBody
        }
        ... on issue__TemplateReleaseRequestedEvent_Type {
          template {
            description
          }
        }
        ... on issue__BillPaymentRequestedEvent_Type {
          acceptedAt
          billPaid
        }
      }
    }
    me {
      id
      webAgentLinks {
        id
        endpoint {
          ... on webAgent__Endpoint_Type {
          type
          }
        }
        executions(order: { createdAt: { dir: DESC } }, first: 10) {
          id
          createdAt
          signInSucceededAt
          latestStatus
          endpoint {
            ... on webAgent__Endpoint_Type {
              type
            }
          }
        }
      }
    }
  }
`);

export const sendMessageMutation = gql(/* GraphQL */ `
  mutation SendMessage($input: CreateIssueChatEventInput!, $userId: ID!) {
    # TODO - ENG-198 - move to a fragment
    createIssueChatEvent(input: $input) {
      __typename
      id
      eventType
      participant
      sentAt
      userVisible
      sentByProfile {
        name
      }
      eventFeedbacks(filter: {owner: {id: {eq: $userId}}}) {
        id
        rating
      }
      ... on issue__ChatEvent_Type {
        chatBody
      }
    }
  }
`);

export const markIssueAsSeenMutation = gql(/* GraphQL */ `
  mutation MarkIssueAsSeen($input: UpdateIssueInput!) {
    updateIssue(input: $input) {
      id
    }
  }
`);

export const selectPlanForEventMutation = gql(/* GraphQL */ `
  mutation selectPlanForEvent($input: SelectPlanForEventInput!) {
    selectPlanForEvent(input: $input) {
      id
    }
  }
`);

export const recordIssueChatEventFeedbackMutation = gql(`
  mutation RecordIssueChatEventFeedback($input: RecordIssueEventFeedbackInput!) {
    recordIssueEventFeedback(input: $input) {
      success
    }
  }
`);

export const conversationEventsSubscription = gql(/* GraphQL */ `
  subscription ConversationEvents($issueId: String!) {
    conversationEvents(issueId: $issueId) {
      type
      turnId
      token
      error
      toolName
    }
  }
`);

export const getChatEventFeedbackOptionsQuery = gql(/* GraphQL */ `
  query GetChatEventFeedbackOptions($area: feedback__AreaType!) {
    feedback__Option(
      filter: {
        area: { eq: $area },
        archived: { eq: false }
      },
      order: { order: { dir: ASC } }
    ) {
      id
      value
      sentiment
    }
  }
`);
